import { useLocation, useNavigate } from "@tanstack/solid-router";
import { createQuery } from "@tanstack/solid-query";
import { Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { isChatConfigured } from "~/lib/config";
import { fetchRoomUnread } from "~/lib/chat";
import { everClaimedTicket, fetchConnections, fetchMemberProfile, fetchProfile, fetchScanQuota } from "~/lib/api";
import { tabsForPass } from "~/lib/meetup";
import { activeTabFromPath, connectionsQueryKey, profileQueryKey } from "~/lib/routes";

type Props = {
  eventId?: string;
};

export function TabBar(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const active = () => activeTabFromPath(location().pathname);
  const eventId = () => props.eventId || "";

  const memberQuery = createQuery(() => ({
    queryKey: ["member-profile"],
    queryFn: fetchMemberProfile,
    retry: false,
  }));

  const profileQuery = createQuery(() => ({
    queryKey: profileQueryKey(eventId()),
    queryFn: () => fetchProfile(eventId() || undefined),
    retry: false,
  }));

  const pass = () => everClaimedTicket(memberQuery.data);
  const tabs = () => tabsForPass(pass());
  const show = (id: "meetups" | "directory" | "opportunities" | "connections" | "me") =>
    tabs().includes(id);

  const quotaQuery = createQuery(() => ({
    queryKey: ["scan-quota", eventId()],
    queryFn: () => fetchScanQuota(eventId()),
    enabled: !!eventId() && everClaimedTicket(profileQuery.data),
    retry: false,
  }));

  const connectionsQuery = createQuery(() => ({
    queryKey: connectionsQueryKey(),
    queryFn: () => fetchConnections(),
    enabled: show("connections"),
    retry: false,
  }));

  const [roomUnread, setRoomUnread] = createSignal(false);
  onMount(() => {
    if (!isChatConfigured()) return;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void fetchRoomUnread().then(setRoomUnread).catch(() => {});
    };
    tick();
    const timer = window.setInterval(tick, 20000);
    document.addEventListener("visibilitychange", tick);
    onCleanup(() => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    });
  });

  const pendingCount = createMemo(() => {
    const items = connectionsQuery.data?.data ?? [];
    return items.filter((c) => c.direction === "incoming" && c.status === "pending").length;
  });

  const isSponsor = () => quotaQuery.data?.data.is_sponsor === true;

  function goDirectory() {
    if (eventId()) {
      navigate({ to: "/directory/$eventId", params: { eventId: eventId() } });
      return;
    }
    navigate({ to: "/directory" });
  }

  function goScan() {
    if (!eventId()) return;
    navigate({ to: "/directory/$eventId/scan", params: { eventId: eventId() } });
  }

  function goLeads() {
    if (!eventId()) return;
    navigate({ to: "/directory/$eventId/leads", params: { eventId: eventId() } });
  }

  function goOpportunities() {
    navigate({ to: "/opportunities" });
  }

  function goConnections() {
    if (eventId()) {
      navigate({ to: "/directory/$eventId/connections", params: { eventId: eventId() } });
      return;
    }
    navigate({ to: "/connections" });
  }

  function goMe() {
    navigate({
      to: "/me",
      search: eventId() ? { fromEvent: eventId() } : {},
    });
  }

  return (
    <nav class="tab-bar" aria-label="Main">
      <Show when={show("meetups")}>
        <button
          type="button"
          class={`tab-bar-item tab-bar-item--meetups${active() === "meetups" ? " tab-bar-item--active" : ""}`}
          onClick={() => navigate({ to: "/meetup" })}
          aria-current={active() === "meetups" ? "page" : undefined}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 8H19V19H5V8Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linejoin="round"
            />
            <path d="M8 8V6H16V8" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
            <path d="M8 12H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <span>Meetups</span>
          <Show when={roomUnread()}>
            <span class="tab-bar-dot" aria-label="Unread room" />
          </Show>
        </button>
      </Show>

      <Show when={show("directory")}>
      <button
        type="button"
        class={`tab-bar-item${active() === "directory" ? " tab-bar-item--active" : ""}`}
        onClick={goDirectory}
        aria-current={active() === "directory" ? "page" : undefined}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 6H20M4 12H20M4 18H14"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <span>Directory</span>
      </button>
      </Show>

      <Show when={show("opportunities")}>
      <button
        type="button"
        class={`tab-bar-item${active() === "opportunities" ? " tab-bar-item--active" : ""}`}
        onClick={goOpportunities}
        aria-current={active() === "opportunities" ? "page" : undefined}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 7H20V19H4V7Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linejoin="round"
          />
          <path d="M8 7V5H16V7" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
          <path d="M4 12H20" stroke="currentColor" stroke-width="2" />
        </svg>
        <span>Opportunities</span>
      </button>
      </Show>

      <Show when={pass() && !!eventId()}>
        <button
          type="button"
          class={`tab-bar-item tab-bar-item--scan${active() === "scan" ? " tab-bar-item--active" : ""}`}
          onClick={goScan}
          aria-current={active() === "scan" ? "page" : undefined}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 7V5a2 2 0 012-2h2M16 3h2a2 2 0 012 2v2M20 17v2a2 2 0 01-2 2h-2M8 21H6a2 2 0 01-2-2v-2M7 12h.01M12 12h.01M17 12h.01M7 17h.01M12 17h.01M17 17h.01"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <span>Scan</span>
        </button>
      </Show>

      <Show when={pass() && !!eventId() && isSponsor()}>
        <button
          type="button"
          class={`tab-bar-item${active() === "leads" ? " tab-bar-item--active" : ""}`}
          onClick={goLeads}
          aria-current={active() === "leads" ? "page" : undefined}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <span>Leads</span>
        </button>
      </Show>

      <Show when={show("connections")}>
      <button
        type="button"
        class={`tab-bar-item tab-bar-item--connections${active() === "connections" ? " tab-bar-item--active" : ""}`}
        onClick={goConnections}
        aria-current={active() === "connections" ? "page" : undefined}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="2" />
          <circle cx="17" cy="9" r="2.5" stroke="currentColor" stroke-width="2" />
          <path
            d="M3 20C3 16.5 5.5 14 9 14C10.5 14 11.8 14.5 12.8 15.3M17 14C19.5 14 21 16 21 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <Show when={pendingCount() > 0}>
          <span class="tab-bar-badge" aria-label={`${pendingCount()} pending requests`}>
            {pendingCount()}
          </span>
        </Show>
        <span>Connections</span>
      </button>
      </Show>

      <button
        type="button"
        class={`tab-bar-item${active() === "me" ? " tab-bar-item--active" : ""}`}
        onClick={goMe}
        aria-current={active() === "me" ? "page" : undefined}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2" />
          <path
            d="M5 20C5 16 8 14 12 14C16 14 19 16 19 20"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <span>Me</span>
      </button>
    </nav>
  );
}
